import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import * as GenericRedux from 'generic/genericRedux'
import { RasaReactComponent } from 'generic/rasaReactComponent'
import React from 'react'
import * as Redux from 'redux'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import { injectReducer } from 'store/index'
import { Dataset } from 'generic/dataset'
import * as Flash from 'components/flash'
import { PreviewWidgetMenuComponent } from '../previewMenuWidget'
import '../styles.css'
import { MenuSection } from '../types'
import {
  MenuItems,
  MenuSections,
  REDUX_STORE_HOME,
} from './constants'
import { HeaderSettings } from './layouts/headerSettings'
import { AppearanceSettings } from './layouts/appearanceSettings'
import { CtaSettings } from './layouts/ctaSettings'
import { TopicsSettings } from './layouts/topicsSettings'
import { PreviewComponent } from './preview'
import './style.scss'
import { BaseClientEntity } from 'generic/baseClientEntity'

type WidgetsTopicPreferencesComponentProps = GenericRedux.AllComponentProps<any>
interface WidgetsTopicPreferencesComponentState {
  communityId: string,
  communityRecordId: string,
  communityGuid: string,
  configurations: any,
  context?: any,
  selected: MenuSection,
  communityTopics: any[],
}

class WidgetsTopicPreferencesComponent extends RasaReactComponent<WidgetsTopicPreferencesComponentProps,
      WidgetsTopicPreferencesComponentState> {
  public static contextType = RasaContext
  private sharedStore: SharedStore

  constructor(props: WidgetsTopicPreferencesComponentProps) {
    super(props, 'widgetsLayout', {
      communityId: '',
      communityRecordId: '',
      communityGuid: '',
      selected: null,
      configurations: null,
      communityTopics: [],
    })
  }

  public componentDidMount = () => {
    this.sharedStore = SharedStore.instance(this.context)
    Promise.all([
      this.sharedStore.getValue(SharedKeys.activeCommunity),
      this.sharedStore.getValue(SharedKeys.hash),
    ])
    .then(([activeCommunity, hash]) => {
      Promise.all([
        this.loadRecord(activeCommunity.communityId, activeCommunity.data.community_id),
        this.loadCommunityTopics(activeCommunity.communityId)
      ])
      .then(([_, topics]) => {
        this.setState({
          communityId: activeCommunity.communityId,
          communityRecordId: activeCommunity.data.community_id,
          communityGuid: activeCommunity.data.community_guid,
          configurations: this.props.data,
          communityTopics: topics || [],
        })
      })
    })
  }

  public render() {
    return (
      <div className="topic-preferences-design">
      {this.state.isLoading ? <Loading size="64" /> :
      <div className="topic-preferences-container">
        <div className="preview">
          <div className="topic-preferences-body"
               style={{backgroundColor: '#FFFFFF', margin: '0px'}}>
              <PreviewComponent configurations={this.state.configurations} data={this.props.data} select={this.state.selected} communityTopics={this.state.communityTopics} />
          </div>
        </div>
        <div className="settings">
          <div className="designbuttonwrap">
            <div className={this.buttonClassNames(this.isDirty)}
                  onClick={() => this.saveRecord(this.state.communityRecordId)}>
              Save
            </div>
          </div>
          <div className="editor">
            {this.getEditor()}
          </div>
        </div>
        <div className="menu">
          <PreviewWidgetMenuComponent
            selectItem={this.selectItem}
            saveRecord={this.saveRecord}
            isDirty={this.isDirty} menuSections={MenuSections}>
          </PreviewWidgetMenuComponent>
        </div>
      </div>
      }
    </div>
    )
  }
  private buttonClassNames = (enabled: boolean): string => {
    return enabled ? 'save-button save-button-enabled clickable-item' :
                     'save-button save-button-disabled'
  }
  private selectItem = (item: MenuSection) => {
    this.setState({
      selected: item,
    })
  }
  private getEditor = () => {
    if (this.state.selected) {
      switch (this.state.selected.value) {
        case MenuItems.headerAndLogo:
          return <HeaderSettings data={this.props.data}
                    name={MenuItems.headerAndLogo}
                    onChange={(field: string, value: string) => this.propertyChanged(field, value)}/>
        case MenuItems.appearance:
          return <AppearanceSettings data={this.props.data}
                    name={MenuItems.appearance}
                    onChange={(field: string, value: string) => this.propertyChanged(field, value)}/>
        case MenuItems.cta:
          return <CtaSettings data={this.props.data}
                    name={MenuItems.cta}
                    onChange={(field: string, value: string) => this.propertyChanged(field, value)}/>
        case MenuItems.topics:
          return <TopicsSettings data={this.props.data}
                    configurations={this.state.configurations}
                    communityTopics={this.state.communityTopics}
                    onChange={(field: string, value: any) => this.propertyChanged(field, value)}
                    onTopicAdd={this.handleTopicAdd}
                    onTopicRemove={this.handleTopicRemove}/>
      }
    } else {
      return <HeaderSettings data={this.props.data}
                name={MenuItems.headerAndLogo}
                onChange={(field: string, value: string) => this.propertyChanged(field, value)}/>
    }
    return <div>No Editor Found</div>
  }

  private loadCommunityTopics = async (communityId: string): Promise<any[]> => {
    try {
      // Use the communityTopics dataset to get topics
      const dataset = new Dataset()
      const params = [
        { param: 'is_excluded', value: '0' }, // Only non-excluded topics
        { param: 'is_custom', value: '1' }    // Only custom (admin-defined) topics
      ]
      
      const response = await dataset.loadCommunityDataset('communityTopics', communityId, params)
      return response && response[0] ? response[0] : []
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load community topics:', error)
      return []
    }
  }

  private handleTopicAdd = (topicName: string) => {
    try {
      // Use entity framework to create community topic
      return this.context.entityMetadata.getEntityObject('communityTopics', this.state.communityId, null)
      .then((topicEntityObject: BaseClientEntity) => {
        topicEntityObject.setRecordId(this.state.communityId, null) // null means create new record
        topicEntityObject.data.communityIdentifier = this.state.communityId
        topicEntityObject.data.topic = topicName
        topicEntityObject.data.options = JSON.stringify({ is_custom: true })
        topicEntityObject.data.is_excluded = 0 // false for admin-defined topics
        return topicEntityObject.save()
        .then(() => {
          this.loadCommunityTopics(this.state.communityId)
          .then((topics) => {
            this.setState({
              communityTopics: topics,
            })
          })
        })
      })
    } catch (error) {
      this.context.store.dispatch(Flash.showFlashError('Failed to add topic. Please try again.'))
    }
      
  }

  private handleTopicRemove = async (topicId: number) => {
    try {
      // Use entity framework to delete community topic (marks as archived)
      const entity = await this.context.entityMetadata.getEntityObject('communityTopics')
      entity.setRecordId(this.state.communityId, topicId)
      const result = await entity.archive()
      if (result) {
        // Remove from state immediately for better UX
        const removedTopic = this.state.communityTopics.find(topic => topic.id === topicId)
        this.setState(prevState => ({
          communityTopics: prevState.communityTopics.filter(topic => topic.id !== topicId)
        }))
        this.context.store.dispatch(Flash.showFlashMessage(`Topic "${removedTopic && removedTopic.topic ? removedTopic.topic : 'Unknown'}" removed successfully`))
      }
    } catch (error) {
      this.context.store.dispatch(Flash.showFlashError('Failed to remove topic. Please try again.'))
    }
  }

}

export const WidgetsTopicPreferences = GenericRedux.createConnect(WidgetsTopicPreferencesComponent, REDUX_STORE_HOME)
injectReducer(
  REDUX_STORE_HOME,
  Redux.combineReducers({
    data: GenericRedux.createGenericReducer(REDUX_STORE_HOME, {}),
  }),
)
